/* Auf den Startbildschirm legen — was die Browser hergeben, und was nicht.
 *
 * Der Wunsch war ein Link, der die App direkt ablegt. **Den gibt es nicht.**
 * Kein Betriebssystem lässt eine Seite ungefragt etwas auf dem Telefon
 * platzieren. Was es gibt:
 *
 *   Android  `beforeinstallprompt` — der Browser sagt der Seite Bescheid, dass
 *            er installieren könnte, und die Seite darf den **echten** Dialog
 *            auslösen. Für den Helfer: ein Knopf, ein Tipp.
 *   iPhone   nichts davon. Safari kennt das Ereignis nicht; installiert wird
 *            über Teilen → Zum Home-Bildschirm, von Hand. Bleibt so, bis Apple
 *            es öffnet.
 *
 * Deshalb hat dieses Modul zwei Ausgänge und keinen dritten: entweder wir haben
 * ein Ereignis in der Hand, oder wir erklären die Schritte.
 */

/** Das Ereignis, das Chrome & Co. feuern. Steht nicht in den DOM-Typen. */
type InstallEreignis = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export type InstallLage =
  | "laeuft_schon"      // die App startet bereits vom Startbildschirm
  | "knopf"             // wir können den echten Dialog auslösen
  | "anleitung_ios"     // iPhone: die drei Schritte zeigen
  | "anleitung_sonst";  // alles andere: allgemeine Schritte

let gemerkt: InstallEreignis | null = null;
const horcher = new Set<() => void>();

/** Wird die App bereits als eigenständige App gestartet? */
export function laeuftAlsApp(): boolean {
  try {
    if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
    // iOS meldet es nicht über display-mode, sondern über ein eigenes Feld.
    return (window.navigator as { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
}

export function istIOS(): boolean {
  try {
    const ua = navigator.userAgent;
    // iPadOS gibt sich seit Version 13 als Mac aus — der Tastendruck verrät es.
    return /iPad|iPhone|iPod/.test(ua)
      || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  } catch {
    return false;
  }
}

/** Einmal beim Start aufrufen: fängt das Ereignis ab, bevor es verfällt.
 *
 *  `preventDefault()` ist wichtig — sonst zeigt Chrome seinen eigenen kleinen
 *  Balken unten, und der Helfer hat zwei Angebote statt einem.
 */
export function installHorchen(): void {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    gemerkt = e as InstallEreignis;
    horcher.forEach(f => f());
  });
  // Nach der Installation ist das Ereignis verbraucht und der Balken erledigt.
  window.addEventListener("appinstalled", () => {
    gemerkt = null;
    horcher.forEach(f => f());
  });
}

/** Für React: sagt Bescheid, wenn sich die Lage ändert. */
export function beiLageaenderung(f: () => void): () => void {
  horcher.add(f);
  return () => { horcher.delete(f); };
}

export function lage(): InstallLage {
  if (laeuftAlsApp()) return "laeuft_schon";
  if (gemerkt) return "knopf";
  return istIOS() ? "anleitung_ios" : "anleitung_sonst";
}

/** Löst den echten Installationsdialog aus. `false` heißt: abgelehnt oder nicht möglich.
 *
 *  Muss **aus einer Nutzergeste heraus** aufgerufen werden — ohne `await`
 *  dazwischen. Dieselbe Regel wie beim Teilen-Dialog: der Browser verlangt,
 *  dass zwischen Tipp und Aufruf nichts liegt.
 */
export async function installieren(): Promise<boolean> {
  const e = gemerkt;
  if (!e) return false;
  // Das Ereignis ist einmalig. Vor dem `await` freigeben, damit ein zweiter
  // Tipp nicht auf einem verbrauchten Ereignis landet.
  gemerkt = null;
  horcher.forEach(f => f());
  try {
    await e.prompt();
    const { outcome } = await e.userChoice;
    return outcome === "accepted";
  } catch {
    return false;
  }
}
